import { useCallback, useRef, useState } from 'react'
import { listEntries } from '@/modules/billing/entries/entriesService'
import type { FinancialEntry } from '@/modules/billing/entries/entriesTypes'

interface BillingEntriesParams {
  unitId: string
  dateFrom: string
  dateTo: string
}

export function useBillingEntries() {
  const [entries, setEntries] = useState<FinancialEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const cacheKeyRef = useRef('')
  const lastParamsRef = useRef<BillingEntriesParams | null>(null)

  const fetchEntries = useCallback(async (unitId: string, dateFrom: string, dateTo: string) => {
    const cacheKey = `${unitId}:${dateFrom}:${dateTo}`

    if (cacheKey === cacheKeyRef.current) return

    setLoading(true)
    setError(null)

    try {
      const data = await listEntries(unitId, {
        dueDateFrom: dateFrom,
        dueDateTo: dateTo,
      })

      setEntries(data)
      cacheKeyRef.current = cacheKey
      lastParamsRef.current = { unitId, dateFrom, dateTo }
    } catch (err) {
      console.error('[useBillingEntries] Error fetching entries:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
      setEntries([])
    } finally {
      setLoading(false)
    }
  }, [])

  const refetchEntries = useCallback(async () => {
    const params = lastParamsRef.current
    if (!params) return

    cacheKeyRef.current = ''

    setLoading(true)
    setError(null)

    try {
      const data = await listEntries(params.unitId, {
        dueDateFrom: params.dateFrom,
        dueDateTo: params.dateTo,
      })

      setEntries(data)
      cacheKeyRef.current = `${params.unitId}:${params.dateFrom}:${params.dateTo}`
    } catch (err) {
      console.error('[useBillingEntries] Error refetching entries:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [])

  const invalidateEntries = useCallback(() => {
    cacheKeyRef.current = ''
    void refetchEntries()
  }, [refetchEntries])

  return {
    entries,
    loading,
    error,
    fetchEntries,
    invalidateEntries,
    refetchEntries,
  }
}
